import { graphql } from 'gatsby';
import { GatsbyImage, getImage, getSrc } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

import ActionCard from '../components/action/card';
import ArticleQuickVersion from '../components/article/article-quick-version';
import ArticlesList from '../components/article/articles-list';
import Framework from '../components/framework';
import Breadcrumb from '../components/framework/breadcrumb';
import Slider from '../components/framework/slider';
import TableOfContent from '../components/framework/table-of-content';
import ProductList from '../components/test/product-list';
import Trans from '../components/translation/trans';
import useSiteMetadata from '../hooks/use_site_metadata';
import useTranslation from '../hooks/use_translation';
import { getVariantByLanguage, handleTableOfContent } from '../utils/helpers';

const Article = ({ data, location }) => {
	const article = data.article;
	const siteMetadata = useSiteMetadata();
	const { language, t } = useTranslation('template_article');
	const variant = getVariantByLanguage(language, article.article_variants);
	const table_of_content = handleTableOfContent(variant.content);
	if (table_of_content.content) {
		variant.content = table_of_content.content;
	}

	const image_header = article?.fields?.files_header_url_src;

	return (
		<Framework
			description={variant.seo_description}
			location={location}
			schema={{
				article: {
					image: getSrc(image_header),
					published_at: article.published_at,
				},
			}}
			tags={variant.seo_keywords}
			title={variant.seo_title}
			tracking={{ content_group: 'Article' }}
			type="article"
		>
			<div className="container mt-lg-3 mt-2 px-0" id="content">
				<nav aria-label="breadcrumb">
					<Breadcrumb
						article={true}
						className=""
						fact={true}
						links={[
							{
								base_url: '/artiklar',
								id:
									article.groups && article.groups.length
										? article.groups[0]
										: null,
								type: 'group',
							},
							{ active: true, title: variant.title },
						]}
					/>
				</nav>
			</div>
			<div className="container article" id="article">
				<div className="article-header grid">
					<div className="grid-intro">
						<h1 className="title-large mb-1">{variant.title}</h1>
						<p className="article-date x-small text-muted">
							{article.published_at.substring(0, 10)}
						</p>
						{image_header && (
							<div className="article-img-container ">
								<GatsbyImage
									alt={variant.title}
									className="img-fluid article-img my-lg-3 my-2 rounded-3 o-hidden"
									image={getImage(image_header)}
								/>
							</div>
						)}
						<p className="lead">
							<Trans defaults={variant.excerpt} t={t} />
						</p>
					</div>
					<div className="grid-side my-3 my-lg-0">
						{article.products.length == 1 && (
							<ProductList
								exclude_groups={[...siteMetadata.config.business_tests_groups]}
								grouping={false}
								include_products={article.products}
								limit={1}
								tracking={{
									item_list_name: 'Products on article item page',
									source: 'related',
								}}
								variant="slim"
							>
								{(component) => (
									<>
										{component && (
											<>
												<div className="mb-3">{component}</div>
											</>
										)}
									</>
								)}
							</ProductList>
						)}
						<ActionCard />
						<TableOfContent items={table_of_content.items} />
					</div>
					<div className="grid-content">
						<div className="article-content">
							{variant.summary && (
								<ArticleQuickVersion content={variant.summary} />
							)}
							<Trans defaults={variant.content} t={t} />
						</div>

						<ProductList
							grouping={false}
							include_products={article.products}
							sort={{ key: 'position', order: 'desc' }}
							tracking={{
								item_list_name: 'Related products on article item page',
								source: 'related',
							}}
							variant="card"
						>
							{(component) => (
								<>
									{component && (
										<>
											<h3 className="title-medium mb-2 mt-5">{t('t2')}</h3>
											<Slider>{component}</Slider>
										</>
									)}
								</>
							)}
						</ProductList>

						<ArticlesList
							cols={6}
							exclude_articles={[article.article_id]}
							include_groups={[article.groups]}
							include_types={['blog']}
							limit={4}
							page="artiklar"
						>
							{(component) => (
								<>
									{component && (
										<div className="mb-5">
											<h3 className="title-medium mb-2 mt-5">{t('t3')}</h3>
											{component}
										</div>
									)}
								</>
							)}
						</ArticlesList>
					</div>
				</div>
			</div>
		</Framework>
	);
};

Article.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};

export default Article;

export const pageQuery = graphql`
	query ArticleById($id: String!) {
		article(id: { eq: $id }) {
			article_id
			fields {
				files_header_url_src {
					childImageSharp {
						gatsbyImageData(width: 980)
					}
				}
			}
			groups
			products
			published_at
			type
			article_variants {
				content
				excerpt
				language {
					code
				}
				seo_description
				seo_keywords
				seo_title
				slug
				summary
				title
			}
		}

		locales: allLocale {
			...translationFields
		}
	}
`;
